import { ToastNotifications } from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import { AddressTypes } from '@vend/business-components/dist/components/EditCustomerModal/DetailsTab/Address'
import { FEATURE_CAPTURE_ADDRESS_SELL_SCREEN } from '../../../../constants'
import { isMarkedAsUnfulfilled } from '../../../../functions/sale'
import { FulfilmentDetail, FulfilmentType } from '../../../../models/fulfilment'
import { LineItem } from '../../../../models/line-item'
import {
  useSaleManager,
  useVdFeatureManager,
} from '../../../../ng-compat/hooks'
import { useAppDispatch, useAppSelector } from '../../../../state/hooks'
import {
  removeFulfilmentState,
  selectCurrentSale,
  selectFulfilmentDetails,
  selectLineItems,
  selectSaleAttributesHasFulfilment,
  selectSaleCustomer,
  selectSaleId,
  setFulfilmentDetails,
  updateLineItem,
  updateLineItems,
  updateSaleStatus,
} from '../../../../state/slices/currentSale/currentSaleSlice'
import {
  openFulfilmentLineItemPopover,
  selectIsSaleCustomerContactInfoReviewed,
} from '../../../../state/slices/uiStateSlice'
import { getFeatureFromCache } from '../../../../util/feature-cache'
import { isMissingContactInfo } from './popover/LineItemFulfillmentUtils'
import { useLineItemFulfillmentAddressDetails } from './popover/hooks/useLineItemFulfillmentAddressDetails'
import { getCustomerAddressDetails } from './fulfillmentHelper'

type useSaleFulfilmentProps = {
  currentLineItem: LineItem
  saleId: string
  hasFulfilment: boolean
  setFulfilmentNote: (note: string) => void
  setFulfilmentType: (fulfilmentType: FulfilmentType, note?: string) => void
  inventoryOnHandQuantity?: number
  fulfilmentDetail?: FulfilmentDetail
}

export const useSaleFulfilment = (
  lineItemLocalId: string
): useSaleFulfilmentProps => {
  const dispatch = useAppDispatch()
  const saleManager = useSaleManager()
  const saleCustomer = useAppSelector(selectSaleCustomer)
  const isContactInfoReviewed = useAppSelector(
    selectIsSaleCustomerContactInfoReviewed
  )
  const fulfilmentDetails = useAppSelector(selectFulfilmentDetails)
  const lineItems = useAppSelector(selectLineItems)
  const saleId = useAppSelector(selectSaleId) || ''
  const feature = useVdFeatureManager()
  const t = useT()
  const currentLineItem = lineItems.find(
    item => item.localId === lineItemLocalId
  )!
  const currentSale = useAppSelector(selectCurrentSale)
  const hasFulfilment = useAppSelector(selectSaleAttributesHasFulfilment)

  const isCaptureAddressSellScreenEnabled = getFeatureFromCache(
    FEATURE_CAPTURE_ADDRESS_SELL_SCREEN
  )
  const {
    isFulfillmentAddressNeedToFix,
  } = useLineItemFulfillmentAddressDetails(fulfilmentDetails[0])

  const setFulfilmentType = (fulfilmentType: FulfilmentType, note?: string) => {
    const otherLineItems = lineItems.filter(
      item => item.localId !== currentLineItem.localId
    )

    // update the line item with the new fulfilment type
    dispatch(updateLineItem({ ...currentLineItem, fulfilmentType }))

    // if we are moving from 'DELIVERY' to 'PICKUP' or viceversa, we need to also move the other line items
    // to keep the fulfilment type synchronized, as we only allow one fulfilment type per sale
    if (
      !!fulfilmentType &&
      fulfilmentType !== FulfilmentType.UNSPECIFIED &&
      currentLineItem.fulfilmentType !== fulfilmentType
    ) {
      const otherLineItemsWithDifferentFulfilmentType = otherLineItems.filter(
        item =>
          !!item.fulfilmentType &&
          item.fulfilmentType !== FulfilmentType.UNSPECIFIED
      )

      if (otherLineItemsWithDifferentFulfilmentType.length > 0) {
        dispatch(
          updateLineItems(
            otherLineItemsWithDifferentFulfilmentType.map(item => ({
              ...item,
              fulfilmentType,
            }))
          )
        )

        if (fulfilmentType !== fulfilmentDetails[0]?.type) {
          ToastNotifications.success(
            t(
              'Fulfillment method updated for all items that require fulfillment'
            )
          )
        }
      }
    }

    // if all line items are deselected, we remove the fulfilment details and reset the sale status
    if (
      isMarkedAsUnfulfilled(currentSale.status) &&
      fulfilmentType === FulfilmentType.UNSPECIFIED &&
      lineItems
        .filter(item => item.localId !== lineItemLocalId)
        .every(
          item =>
            !item.fulfilmentType ||
            item.fulfilmentType === FulfilmentType.UNSPECIFIED
        )
    ) {
      dispatch(removeFulfilmentState())
      dispatch(updateSaleStatus(undefined))
    }

    if (fulfilmentType !== FulfilmentType.UNSPECIFIED) {
      const newFulfilmentDetail: FulfilmentDetail = {
        type: fulfilmentType,
        billingAddress: fulfilmentDetails[0]?.billingAddress,
        shippingAddress: fulfilmentDetails[0]?.shippingAddress,
        note: fulfilmentDetails[0]?.note || '',
      }
      if (note) {
        newFulfilmentDetail.note = note
      }
      if (
        isCaptureAddressSellScreenEnabled &&
        fulfilmentType === FulfilmentType.DELIVERY &&
        saleCustomer
      ) {
        if (!newFulfilmentDetail.billingAddress) {
          newFulfilmentDetail.billingAddress = getCustomerAddressDetails(
            saleCustomer,
            AddressTypes.Billing
          )
        }
        if (!newFulfilmentDetail.shippingAddress) {
          newFulfilmentDetail.shippingAddress = getCustomerAddressDetails(
            saleCustomer,
            AddressTypes.Shipping
          )
        }

        // If Shipping or Billing address is not valid, we open the popover
        if (isFulfillmentAddressNeedToFix(newFulfilmentDetail)) {
          dispatch(openFulfilmentLineItemPopover(lineItemLocalId))
        }
      }
      dispatch(setFulfilmentDetails([newFulfilmentDetail]))
    }

    switch (fulfilmentType) {
      case FulfilmentType.PICKUP:
        saleManager.unfulfilledPickUp()
        break
      case FulfilmentType.DELIVERY:
        saleManager.unfulfilledDelivery()
        break
      default:
        break
    }

    if (
      feature.get('fulfillment_lif_b3') &&
      (!saleCustomer ||
        isMissingContactInfo(saleCustomer) ||
        !isContactInfoReviewed)
    ) {
      if (fulfilmentType !== FulfilmentType.UNSPECIFIED) {
        dispatch(openFulfilmentLineItemPopover(lineItemLocalId))
      } else {
        dispatch(openFulfilmentLineItemPopover(''))
      }
    }

    saleManager.saveSale()
  }

  const setFulfilmentNote = (note: string) => {
    dispatch(setFulfilmentDetails([{ ...fulfilmentDetails[0], note }]))
  }

  return {
    currentLineItem,
    saleId,
    hasFulfilment,
    setFulfilmentType,
    setFulfilmentNote,
    fulfilmentDetail: fulfilmentDetails[0],
  }
}
